import { parse } from 'papaparse';
import {
  GOOGLE_SHEETS_URL,
  GOOGLE_SHEETS_METADATA_URL,
  CHART_DATA_TIME_PERIOD,
} from './constants.js';

const now = Date.now();
const THREE_MONTHS_MS = 1000 * 60 * 60 * 24 * CHART_DATA_TIME_PERIOD;

// Fetch metadata (like Historical Cutoff) from the metadata sheet
async function fetchMetadata() {
  return new Promise((resolve) => {
    const metadata = {};

    parse(GOOGLE_SHEETS_METADATA_URL, {
      download: true,
      header: true,
      complete: (results) => {
        if (results.errors.length) {
          console.warn('Metadata parsing errors:', results.errors);
        }

        // Convert rows to key-value pairs
        results.data.forEach((row) => {
          if (row.Setting && row.Value) {
            metadata[row.Setting] = row.Value;
          }
        });

        resolve(metadata);
      },
      error: (err) => {
        console.log('Metadata fetch failed, using fallback:', err);
        resolve({}); // Return empty metadata to allow fallback
      },
    });
  });
}

// Helper function to create cleaned row data
const createCleanedRow = (rowData) => ({
  Date: rowData.Date,
  Estimate: typeof rowData.Estimate === 'number' ? rowData.Estimate : 0,
  '5th Percentile':
    typeof rowData['5th Percentile'] === 'number'
      ? rowData['5th Percentile']
      : 0,
  '95th Percentile':
    typeof rowData['95th Percentile'] === 'number'
      ? rowData['95th Percentile']
      : 0,
  isProjected: false,
});

// Helper function to check if row has valid data
const isValidRow = (rowData) => {
  const timestamp = new Date(rowData.Date).getTime();
  return !!(
    rowData.Date &&
    rowData.Estimate &&
    !isNaN(timestamp) &&
    now - timestamp <= THREE_MONTHS_MS
  );
};

async function fetchAndCleanData(customUrl = null, customMetadata = null) {
  // Fetch metadata first to get historical cutoff
  let metadata = customMetadata;
  if (!metadata) {
    try {
      metadata = await fetchMetadata();
    } catch (err) {
      console.log('Metadata fetch failed, using fallback:', err);
      metadata = {};
    }
  }

  const urlToUse = customUrl || GOOGLE_SHEETS_URL;

  return new Promise((resolve, reject) => {
    const rows = [];
    let historicalCutoffDate = null;
    const nonNumericalValues = [];

    // Parse historical cutoff from metadata
    if (metadata['Historical Cutoff']) {
      try {
        historicalCutoffDate = new Date(metadata['Historical Cutoff']);
        console.log(
          'Using historical cutoff from metadata:',
          historicalCutoffDate.toISOString().split('T')[0],
        );
      } catch (e) {
        console.log(
          'Invalid historical cutoff in metadata:',
          metadata['Historical Cutoff'],
        );
      }
    }

    parse(urlToUse, {
      download: true,
      header: true,
      dynamicTyping: true,
      workers: true,
      chunkSize: 1024 * 512,
      skipEmptyLines: true,
      step: (result) => {
        const r = result.data;

        if (isValidRow(r)) {
          // Track if we encounter any non-numerical values (should be rare now)
          if (nonNumericalValues.length === 0) {
            const hasInvalidValue = (value) =>
              typeof value !== 'number' ||
              isNaN(value) ||
              value === null ||
              value === undefined;

            if (
              hasInvalidValue(r.Estimate) ||
              hasInvalidValue(r['5th Percentile']) ||
              hasInvalidValue(r['95th Percentile'])
            ) {
              console.log('Non-numerical data detected, using fallback values');
              nonNumericalValues.push(true);
            }
          }

          const cleanedRow = createCleanedRow(r);
          if (
            typeof cleanedRow.Estimate === 'number' &&
            cleanedRow.Estimate > 0
          ) {
            rows.push(cleanedRow);
          }
        }
      },
      complete: (results) => {
        if (results.errors.length) {
          console.error('CSV parsing errors:', results.errors.length);
        }

        if (rows.length === 0) {
          reject(new Error('No valid data rows found'));
          return;
        }

        // Use fallback cutoff if not found in metadata
        if (!historicalCutoffDate) {
          historicalCutoffDate = new Date();
          historicalCutoffDate.setDate(historicalCutoffDate.getDate() - 14);
          console.log(
            'No historical cutoff in metadata, using 14-day fallback',
          );
        }

        // Determine projected flags based on cutoff
        rows.forEach((row) => {
          const rowDate = new Date(row.Date);
          row.isProjected = rowDate > historicalCutoffDate;
        });

        const sortedRows = rows.sort(
          (a, b) => new Date(a.Date) - new Date(b.Date),
        );
        resolve(sortedRows);
      },
      error: (err) => reject(err),
    });
  });
}

export default fetchAndCleanData;
